import { useEffect, useState } from 'react'
import { ITableTh } from '../components/common/table/Table'
import { TRole } from '../model/IRole'
import { useTypedSelector } from './useTypedSelector'
import {
  getTiketsTableFields,
  setFields,
  TTicketsTableColOption,
} from '../store/ticketsFilterSlice'
import { useUser } from './useUser'
import { useTypedDispatch } from './useTypedDispatch'

export type TTicketTableCols =
  | ''
  | 'Ticket #'
  | 'Priority'
  | 'Status'
  | 'Action Required By'
  | 'Type'
  | 'Customer'
  | 'Order #'
  // | 'Vendor Action'
  | 'Vendor Name'
  | 'Vendor Order #'
  | 'Last Updated By'
  | 'Created'
  | 'Last Updated'
  | 'Assigned Vendor Group'
  | 'Assigned Agent Group'

const allTablesCols: Record<TTicketTableCols, ITableTh> = {
  '': { title: '' },
  'Ticket #': { title: 'Ticket #', sortCol: 'id' },
  Priority: { title: 'Priority', sortCol: 'priority' },
  Status: { title: 'Status', sortCol: 'status', align: 'center' },
  'Action Required By': { title: 'Action Required By', align: 'center' },
  Type: { title: 'Type', sortCol: 'type' },
  Customer: { title: 'Customer' },
  'Order #': { title: 'Order #', sortCol: 'orderId' },
  'Vendor Name': { title: 'Vendor Name', align: 'center',sortCol: 'vendor.name'  },
  'Vendor Order #': { title: 'Vendor Order #' },
  'Last Updated By': { title: 'Last Updated By' },
  Created: { title: 'Created', sortCol: 'created' },
  'Last Updated': { title: 'Last Updated', sortCol: 'updated' },
  'Assigned Vendor Group': { title: 'Assigned Vendor Group', sortCol: 'assignedVendorGroup' },
  'Assigned Agent Group': { title: 'Assigned Agent Group', sortCol: 'assignedAgentGroup' },
}

const tableColViews: { accessRoles: TRole[]; cols: (keyof typeof allTablesCols)[] }[] = [
  {
    accessRoles: ['ROLE_ADMIN'],
    cols: [
      'Ticket #',
      'Status',
      'Action Required By',
      'Priority',
      'Type',
      'Customer',
      'Order #',
      'Vendor Order #',
      'Vendor Name',
      'Last Updated By',
      'Created',
      'Last Updated',
      'Assigned Agent Group',
      'Assigned Vendor Group',
    ],
  },
  {
    accessRoles: ['ROLE_VENDOR'],
    cols: [
      'Ticket #',
      'Status',
      'Action Required By',
      'Priority',
      'Type',
      'Customer',
      'Vendor Order #',
      'Vendor Name',
      'Order #',
      'Last Updated By',
      'Created',
      'Last Updated',
      'Assigned Vendor Group',
    ],
  },
  {
    accessRoles: ['ROLE_AGENT'],
    cols: [
      'Ticket #',
      'Status',
      'Action Required By',
      'Priority',
      'Type',
      'Customer',
      'Order #',
      'Vendor Order #',
      'Vendor Name',
      'Last Updated By',
      'Created',
      'Last Updated',
      'Assigned Agent Group',
      'Assigned Vendor Group',
    ],
  },
]

export function useTicketTableCols() {
  const [theadCols, setTheadCols] = useState<TTicketTableCols[]>()

  const dispatch = useTypedDispatch()
  const tableColsFilter = useTypedSelector(getTiketsTableFields())
  const { hasRoles } = useUser()

  // --
  // -- Determine thead cols set depends on the currents roles
  // -- And set the calculated set to global state
  // --
  useEffect(() => {
    if (!tableColsFilter) {
      const theadCols = tableColViews.find(({ accessRoles }) => hasRoles(accessRoles))?.cols || []

      setTheadCols(() => theadCols)
      dispatch(
        setFields(
          theadCols.reduce(
            (acc, col) => ({
              ...acc,
              [col]: true,
            }),
            {} as TTicketsTableColOption,
          ),
        ),
      )
    }
  }, [])

  // --
  // -- If fields filter changes -> change table view
  // --
  useEffect(() => {
    tableColsFilter &&
      Object.values(tableColsFilter)?.length &&
      setTheadCols(
        () =>
          Object.entries(tableColsFilter)
            .filter(([_, active]) => active)
            .map(([key, _]) => key) as TTicketTableCols[],
      )
  }, [tableColsFilter])

  return { theadCols, allTablesCols }
}
